import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiUrl from '../../Config';

const AddEditBanner = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const banner = location?.state?.banners || '';
    const [formData, setFormData] = useState({
        id: '',
        bannerTitle: '',
        bannerDescription: '',
        bannerImg: null,
        isActive: false,
        isDeleted: false,
        bannerOrder: '',
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [validateErrors, setValidateErrors] = useState({})
    useEffect(() => {
        if (banner) {
            setFormData({
                id: banner.id || '',
                bannerTitle: banner.bannerTitle || '',
                bannerDescription: banner.bannerDescription || '',
                bannerImg: null,
                isActive: banner.isActive || false,
                isDeleted: banner.isDeleted || false,
                bannerOrder: banner.bannerOrder || '',
            });

            // Set image preview if available
            if (banner.bannerUrl && typeof banner.bannerUrl === 'string') {
                setImagePreview(banner.bannerUrl);
            } else if (banner.bannerUrl) {
                setImagePreview(banner.bannerUrl);
            }
        }
    }, [banner]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Fields that should only allow numeric input (digits only)
        if (name === 'bannerOrder') {

            if (value === '' || /^[0-9\b]+$/.test(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
            setValidateErrors(prev => ({ ...prev, [name]: '' }));
        }

        setError('');
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, bannerImg: file }));
            setImagePreview(URL.createObjectURL(file));
            setValidateErrors(prev => ({ ...prev, bannerImg: '' }));
            setError('');
        }
    };
    const validateForm = () => {
        const errors = {};

        if (!formData.bannerTitle?.trim()) {
            errors.bannerTitle = "Banner title is required";
        }

        if (!formData.bannerDescription?.trim()) {
            errors.bannerDescription = "Banner description is required";
        }

        if (
            formData.bannerOrder === '' ||
            isNaN(formData.bannerOrder)
        ) {
            errors.bannerOrder = "Valid banner order is required";
        }

        if (!formData.bannerImg && !imagePreview) {
            errors.bannerImg = "Please upload a valid banner image";
        }

        return errors;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidateErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const payload = new FormData();
            payload.append('id', formData.id);
            payload.append('bannerTitle', formData.bannerTitle);
            payload.append('bannerDescription', formData.bannerDescription);
            payload.append('bannerOrder', formData.bannerOrder);
            payload.append('isActive', formData.isActive);
            payload.append('isDeleted', formData.isDeleted);

            if (formData.bannerImg instanceof File) {
                payload.append('bannerImage', formData.bannerImg);
            } else if (typeof imagePreview === 'string') {
                payload.append('bannerImage', imagePreview);
            }

            const isEditMode = !!formData.id;

            const response = isEditMode
                ? await axios.put(`${apiUrl}/banners/updatebanner/${formData.id}`, payload)
                : await axios.post(`${apiUrl}/banners/createbanner`, payload);

            if (response?.data?.status === true) {
                navigate(-1);
                toast.success(response?.data?.message);
            }
        } catch (error) {
            setError(error?.response?.data?.message || 'Something went wrong. Please try again later.');
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <Box sx={{ p: { xs: 2, sm: 2 } }}>
                <Typography variant="h4" fontWeight={400} mb={1} >
                    {banner ? `Edit Banner - ${formData.bannerTitle}` : 'Add New Banner'}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>

                        {/* Image Upload */}
                        <Grid item size={{ xs: 12, sm: 12 }}>
                            <Typography variant="body1" fontWeight={500} gutterBottom>
                                Banner Image
                            </Typography>
                            <Box
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 2,
                                    bgcolor: 'primary.background',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: validateErrors.bannerImg ? '1px solid red' : '1px dashed gray',
                                    mb: 1,
                                }}
                            >
                                {imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ width: '110px', height: '110px', borderRadius: 4 }}
                                    />
                                ) : (
                                    <Typography variant="caption">No Image</Typography>
                                )}
                            </Box>

                            <label htmlFor="upload-img">
                                <input
                                    type="file"
                                    id="upload-img"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageFileChange}
                                />
                                <Button variant="outlined" component="span" type="button">
                                    Upload Image
                                </Button>
                            </label>
                        </Grid>

                        {/* Banner Title */}
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Banner Title"
                                name="bannerTitle"
                                fullWidth
                                size="small"
                                value={formData.bannerTitle}
                                onChange={handleInputChange}
                                error={!!validateErrors.bannerTitle}
                                helperText={validateErrors.bannerTitle}
                            />
                        </Grid>

                        {/* Banner Description */}
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Banner Description"
                                name="bannerDescription"
                                fullWidth
                                size="small"
                                value={formData.bannerDescription}
                                onChange={handleInputChange}
                                error={!!validateErrors.bannerDescription}
                                helperText={validateErrors.bannerDescription}
                            />
                        </Grid>

                        {/* Banner Order */}
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Banner Order"
                                name="bannerOrder"
                                fullWidth
                                size="small"
                                value={formData.bannerOrder}
                                onChange={handleInputChange}
                                error={!!validateErrors.bannerOrder}
                                helperText={validateErrors.bannerOrder}
                            />
                        </Grid>

                        {/* Checkboxes */}
                        <Grid item size={{ xs: 12, sm: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleCheckboxChange}
                                        size="small"
                                    />
                                }
                                label="Is Active"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isDeleted"
                                        checked={formData.isDeleted}
                                        onChange={handleCheckboxChange}
                                        size="small"
                                    />
                                }
                                label="Is Deleted"
                            />
                        </Grid>

                        {/* Error Message */}
                        {error && (
                            <Grid item size={12}>
                                <Typography color="error">{error}</Typography>
                            </Grid>
                        )}

                        {/* Submit Button */}
                        <Grid item size={12}>
                            <Box display="flex" justifyContent="end" mt={2} gap={2}>
                                <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" rangeFor>
                                    {loading ? (
                                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                                    ) : (
                                        banner ? 'Update' : 'Create'
                                    )}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Box>

        </>
    )
}

export default AddEditBanner